import React from "react";
import {
  TextField,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  MenuItem,
  InputAdornment,
} from "@material-ui/core";
import {
  KeyboardDatePicker,
  KeyboardTimePicker,
  KeyboardDateTimePicker,
} from "@material-ui/pickers";
import NumberFormat from "react-number-format";
import PropTypes from "prop-types";
import { getIn } from "formik";
import { useGlobalData } from "../../../provider/GlobalDataProvider";
import { DateTime } from "luxon";
function CurrencyFormat(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            value: values.value,
          },
        });
      }}
      decimalScale={2}
      thousandSeparator
      isNumericString
      prefix=""
    />
  );
}

CurrencyFormat.propTypes = {
  inputRef: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export const CustomTextField = ({
  label,
  inputType,
  propertyName,
  onBlur,
  onChange,
  values,
  touched,
  errors,
  size,
  margin,
  endAdornment,
  variant,
  onClick,
  disabled,
  upperCase,
}) => {
  if (typeof margin === "undefined") {
    margin = "normal";
  }
  if (typeof size === "undefined") {
    size = "medium";
  }
  if (typeof variant === "undefined") {
    variant = "outlined";
  }
  if (typeof endAdornment === "undefined") {
    endAdornment = {};
  }

  let whiteBackground = false;
  if (typeof endAdornment.endAdornment !== "undefined") {
    whiteBackground = true;
  }
  if (typeof upperCase === "undefined") {
    upperCase = "none";
  }
  return (
    <TextField
      type={inputType}
      label={label}
      margin={margin}
      size={size}
      variant={variant}
      disabled={disabled}
      style={{ width: "100%" }}
      name={propertyName}
      onBlur={onBlur}
      onChange={onChange}
      value={values[propertyName]}
      helperText={touched[propertyName] && errors[propertyName]}
      error={Boolean(touched[propertyName] && errors[propertyName])}
      InputProps={endAdornment}
      onClick={onClick}
      inputProps={{
        style: {
          //backgroundColor: whiteBackground ? "white" : null,
          textTransform: upperCase,
        },
      }}
      InputLabelProps={{ shrink: true }}
    />
  );
};

export const CustomTextAreaField = ({
  label,
  inputType,
  propertyName,
  onBlur,
  onChange,
  values,
  touched,
  errors,
  size,
  margin,
  endAdornment,
  variant,
  onClick,
  minRows,
  disabled,
}) => {
  if (typeof margin === "undefined") {
    margin = "normal";
  }
  if (typeof size === "undefined") {
    size = "medium";
  }
  if (typeof variant === "undefined") {
    variant = "outlined";
  }
  if (typeof endAdornment === "undefined") {
    endAdornment = {};
  }
  return (
    <TextField
      type={inputType}
      label={label}
      margin={margin}
      size={size}
      variant={variant}
      disabled={disabled}
      className="kt-width-full"
      name={propertyName}
      onBlur={onBlur}
      onChange={onChange}
      value={values[propertyName]}
      helperText={touched[propertyName] && errors[propertyName]}
      error={Boolean(touched[propertyName] && errors[propertyName])}
      InputProps={endAdornment}
      onClick={onClick}
      multiline
      minRows={minRows}
      InputLabelProps={{ shrink: true }}
    />
  );
};

export const CustomCurrencyField = ({
  label,
  propertyName,
  readOnly,
  onBlur,
  values,
  touched,
  errors,
  size,
  margin,
  variant,
  disabled,
}) => {
  const { companyProfile } = useGlobalData();
  if (typeof readOnly === "undefined") {
    readOnly = false;
  }
  if (typeof size === "undefined") {
    size = "medium";
  }
  if (typeof margin === "undefined") {
    margin = "normal";
  }

  if (typeof variant === "undefined") {
    variant = "outlined";
  }

  return (
    <TextField
      type="text"
      label={label}
      margin={margin}
      size={size}
      variant={variant}
      disabled={disabled}
      className="kt-width-full"
      name={propertyName}
      onBlur={onBlur}
      onChange={(event) => {
        values[propertyName] = event.target.value;
      }}
      value={values[propertyName]}
      helperText={touched[propertyName] && errors[propertyName]}
      error={Boolean(touched[propertyName] && errors[propertyName])}
      InputProps={{
        inputComponent: CurrencyFormat,
        readOnly: readOnly,
        startAdornment: (
          <InputAdornment position="start">
            {companyProfile.currency.currencyCode}
          </InputAdornment>
        ),
      }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ style: { textAlign: "right" } }}
    />
  );
};

export const CustomSelectField = ({
  label,
  propertyName,
  onBlur,
  onChange,
  values,
  touched,
  errors,
  options,
  size,
  margin,
  readOnly,
  variant,
  disabled,
}) => {
  const inputLabel = React.useRef(null);
  const [labelWidth, setLabelWidth] = React.useState(0);
  React.useEffect(() => {
    setLabelWidth(inputLabel.current.offsetWidth);
  }, []);
  if (typeof margin === "undefined") {
    margin = "normal";
  }
  if (typeof size === "undefined") {
    size = "medium";
  }
  if (typeof variant === "undefined") {
    variant = "outlined";
  }
  return (
    <FormControl
      variant={variant}
      disabled={disabled}
      error={Boolean(touched[propertyName] && errors[propertyName])}
      size={size}
      margin={margin}
    >
      <InputLabel ref={inputLabel} id={`${propertyName}-label`}>
        {label}
      </InputLabel>
      <Select
        labelId={`${propertyName}-label`}
        id={propertyName}
        name={propertyName}
        value={values[propertyName]}
        onChange={onChange}
        onBlur={onBlur}
        labelWidth={labelWidth}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {Boolean(touched[propertyName] && errors[propertyName]) ? (
        <FormHelperText>{errors[propertyName]}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export const CustomDatePickerField = ({ field, form, ...other }) => {
  const currentError = form.errors[field.name];
  return (
    <KeyboardDatePicker
      autoOk
      disablePast={other.disablePast}
      disableFuture={other.disableFuture}
      inputVariant="outlined"
      variant="inline"
      name={field.name}
      value={field.value}
      onBlur={field.onBlur}
      format="yyyy-MM-dd"
      helperText={other.touched[field.name] && currentError}
      error={Boolean(other.touched[field.name] && currentError)}
      onError={(error) => {
        if (error !== currentError) {
          form.setFieldError(field.name, error);
        }
      }}
      onChange={(date) => {
        form.setFieldValue(field.name, date, true);
      }}
      InputProps={{ readOnly: true }}
      {...other}
    />
  );
};

export const CustomTimePickerField = ({ field, form, ...other }) => {
  const currentError = form.errors[field.name];
  return (
    <KeyboardTimePicker
      autoOk
      disablePast={other.disablePast}
      disableFuture={other.disableFuture}
      inputVariant="outlined"
      variant="inline"
      name={field.name}
      value={field.value}
      onBlur={field.onBlur}
      //format="yyyy-MM-dd"
      helperText={other.touched[field.name] && currentError}
      error={Boolean(other.touched[field.name] && currentError)}
      onError={(error) => {
        if (error !== currentError) {
          form.setFieldError(field.name, error);
        }
      }}
      onChange={(date) => {
        if (date instanceof Date && !isNaN(date)) {
          form.setFieldValue(
            field.name,
            DateTime.fromISO(date.toISOString()),
            true
          );
        } else if (date === null) {
          form.setFieldValue(field.name, DateTime.now(), true);
        }
      }}
      {...other}
    />
  );
};

export const CustomDateTimePickerField = ({ field, form, ...other }) => {
  const currentError = form.errors[field.name];
  return (
    <KeyboardDateTimePicker
      autoOk
      disablePast={other.disablePast}
      disableFuture={other.disableFuture}
      inputVariant="outlined"
      variant="inline"
      name={field.name}
      value={field.value}
      onBlur={field.onBlur}
      format="yyyy-MM-dd hh:mm"
      helperText={other.touched[field.name] && currentError}
      error={Boolean(other.touched[field.name] && currentError)}
      onError={(error) => {
        if (error !== currentError) {
          form.setFieldError(field.name, error);
        }
      }}
      onChange={(date) => {
        if (date instanceof Date && !isNaN(date)) {
          form.setFieldValue(
            field.name,
            DateTime.fromISO(date.toISOString()),
            true
          );
        } else if (date === null) {
          form.setFieldValue(field.name, DateTime.now(), true);
        }
      }}
      {...other}
    />
  );
};

export const CustomArrayTextField = ({
  label,
  arrayName,
  inputType,
  propertyName,
  onBlur,
  onChange,
  index,
  value,
  touched,
  errors,
  endAdornment,
  disabled,
  margin = "none",
}) => {
  const field = `${arrayName}[${index}].${propertyName}`;
  const touchedProperty = getIn(touched, field);
  const errorsProperty = getIn(errors, field);
  if (typeof inputType === "undefined") {
    inputType = "text";
  }
  let variant = "outlined";
  let whiteBackground = false;
  if (typeof endAdornment !== "undefined") {
    if (typeof endAdornment.endAdornment !== "undefined") {
      whiteBackground = true;
    }
  }

  return (
    <TextField
      label={label}
      type={inputType}
      variant={variant}
      disabled={disabled}
      margin={margin}
      fullWidth
      size="small"
      style={{ width: "100%" }}
      name={field}
      onBlur={onBlur}
      onChange={onChange}
      value={value[propertyName]}
      helperText={touchedProperty && errorsProperty}
      error={Boolean(touchedProperty && errorsProperty)}
      InputProps={endAdornment}
      inputProps={{
        style: {
          //backgroundColor: whiteBackground ? "white" : null,
        },
      }}
    />
  );
};

export const CustomArrayCurrencyField = ({
  arrayName,
  propertyName,
  onBlur,
  index,
  value,
  touched,
  errors,
  readOnly,
  disabled,
}) => {
  const { companyProfile } = useGlobalData();
  const field = `${arrayName}[${index}].${propertyName}`;
  const touchedProperty = getIn(touched, field);
  const errorsProperty = getIn(errors, field);
  if (typeof readOnly === "undefined") {
    readOnly = false;
  }
  let variant = "outlined";
  return (
    <TextField
      type="text"
      variant={variant}
      disabled={disabled}
      className="kt-width-full"
      size="small"
      style={{ width: "100%" }}
      name={field}
      onBlur={onBlur}
      onChange={(event) => {
        value[propertyName] = event.target.value;
      }}
      value={value[propertyName]}
      helperText={touchedProperty && errorsProperty}
      error={Boolean(touchedProperty && errorsProperty)}
      InputProps={{
        inputComponent: CurrencyFormat,
        readOnly: readOnly,
        startAdornment: (
          <InputAdornment position="start">
            {companyProfile.currency.currencyCode}
          </InputAdornment>
        ),
      }}
      inputProps={{ style: { textAlign: "right" } }}
    />
  );
};

export const CustomArraySelectField = ({
  arrayName = "",
  arrayName2 = "",
  propertyName,
  onBlur,
  onChange,
  index,
  index2,
  value,
  touched,
  errors,
  options,
}) => {
  let fieldValue = "";
  if (arrayName2 !== "") {
    fieldValue = value[arrayName][index][arrayName2][index2][propertyName];
  } else {
    fieldValue = value[arrayName][index][propertyName];
  }
  const field = `${arrayName}[${index}].${propertyName}`;
  const touchedProperty = getIn(touched, field);
  const errorsProperty = getIn(errors, field);
  return (
    <FormControl
      variant="outlined"
      style={{
        width: "100%",
      }}
      size="small"
      error={Boolean(touchedProperty && errorsProperty)}
    >
      <Select
        id={propertyName}
        name={field}
        value={fieldValue}
        onChange={onChange}
        onBlur={onBlur}
      >
        {options.map((option, index) => (
          <MenuItem
            key={index}
            selected={option.value === value[propertyName] ? true : false}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {Boolean(touchedProperty && errorsProperty) ? (
        <FormHelperText>{errorsProperty}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export const CustomFilledReadOnlyTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            variant="filled"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomOutlinedReadOnlyTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomStandardReadOnlyTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            variant="standard"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomFilledDisabledTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            disabled
            variant="filled"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomOutlinedDisabledTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            disabled
            variant="outlined"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomStandardDisabledTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            disabled
            variant="standard"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomFilledReadOnlyCurrencyField = (props) => {
  const { companyProfile } = useGlobalData();
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              inputComponent: CurrencyFormat,
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  {companyProfile.currency.currencyCode}
                </InputAdornment>
              ),
            }}
            variant="filled"
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomOutlinedReadOnlyCurrencyField = (props) => {
  const { companyProfile } = useGlobalData();
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              inputComponent: CurrencyFormat,
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  {companyProfile.currency.currencyCode}
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomStandardReadOnlyCurrencyField = (props) => {
  const { companyProfile } = useGlobalData();
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              inputComponent: CurrencyFormat,
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  {companyProfile.currency.currencyCode}
                </InputAdornment>
              ),
            }}
            variant="standard"
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomFilledDisabledCurrencyField = (props) => {
  const { companyProfile } = useGlobalData();
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              inputComponent: CurrencyFormat,
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  {companyProfile.currency.currencyCode}
                </InputAdornment>
              ),
            }}
            disabled
            variant="filled"
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomOutlinedDisabledCurrencyField = (props) => {
  const { companyProfile } = useGlobalData();
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              inputComponent: CurrencyFormat,
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  {companyProfile.currency.currencyCode}
                </InputAdornment>
              ),
            }}
            disabled
            variant="outlined"
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomStandardDisabledCurrencyField = (props) => {
  const { companyProfile } = useGlobalData();
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            InputProps={{
              inputComponent: CurrencyFormat,
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  {companyProfile.currency.currencyCode}
                </InputAdornment>
              ),
            }}
            disabled
            variant="standard"
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomOutlinedReadOnlyNoMarginTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="none"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomOutlinedDisabledNoMarginTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="none"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            disabled
            variant="outlined"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomOutlinedReadOnlyNoMarginCurrencyField = (props) => {
  const { companyProfile } = useGlobalData();
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="none"
            fullWidth
            InputProps={{
              inputComponent: CurrencyFormat,
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  {companyProfile.currency.currencyCode}
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const CustomOutlinedDisabledNoMarginCurrencyField = (props) => {
  const { companyProfile } = useGlobalData();
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="none"
            fullWidth
            InputProps={{
              inputComponent: CurrencyFormat,
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  {companyProfile.currency.currencyCode}
                </InputAdornment>
              ),
            }}
            disabled
            variant="outlined"
            size="small"
            inputProps={{ style: { textAlign: "right" } }}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};
